import { useEffect, useState } from 'react';
import { c } from '../../theme';

// The Executive Overview's live clock — the old page's own header chip: a
// pulsing dot, the word LIVE, the time to the second, and the date under it.
//
// It exists because the page is put on a screen in a room and read from a
// distance, where "is this still updating?" is the first question. A ticking
// second hand answers it without anyone touching anything, which a "data as
// of" stamp beside it does not.
//
// ── It is Pacific, not the reader's clock ───────────────────────────────────
// The old page labels this "PT" and then formats `new Date()` with no time
// zone at all, so it shows the READER's local time under a Pacific label.
// On a laptop in another zone it is confidently wrong — and this page is read
// by people who travel. Every date on this dashboard is a Pacific date
// (TZ=America/Los_Angeles is load-bearing across the product), so the label
// is the thing worth keeping and the formatting is fixed to match it.
//
// The interval is cleared on unmount. A second's tick on a page nobody is
// looking at is a wasted render, and a timer left running after the route
// changes is the kind of leak that only shows up after an hour on a wall
// display.

const TIME = {
  timeZone: 'America/Los_Angeles',
  hour: 'numeric', minute: '2-digit', second: '2-digit',
};
const DATE = {
  timeZone: 'America/Los_Angeles',
  weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
};

export default function ExecLiveClock() {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div style={s.box}>
      <style>{css}</style>
      <div style={s.row}>
        <span className="cc-live-dot" style={s.dot} aria-hidden="true" />
        <span style={s.live}>LIVE</span>
        {/* One element, one accessible reading: a screen reader should hear
            the time once, not the digits re-announced every second. */}
        <strong style={s.time} aria-live="off">
          {now.toLocaleTimeString('en-US', TIME)} PT
        </strong>
      </div>
      <div style={s.date}>{now.toLocaleDateString('en-US', DATE)}</div>
    </div>
  );
}

// The dot pulses, which inline styles cannot express — the pattern the portal
// uses elsewhere. It is disabled for anyone who has asked for less motion:
// the chip still reads LIVE, so nothing is lost but the animation.
const css = `
  @keyframes ccLivePulse {
    0%, 100% { opacity: 1; }
    50%      { opacity: 0.3; }
  }
  .cc-live-dot { animation: ccLivePulse 1.6s ease-in-out infinite; }
  @media (prefers-reduced-motion: reduce) { .cc-live-dot { animation: none; } }
`;

const s = {
  box:  { textAlign: 'right' },
  row:  { display: 'flex', alignItems: 'center', gap: '7px', justifyContent: 'flex-end' },
  dot:  { width: '7px', height: '7px', borderRadius: '50%', background: c.brand },
  live: { fontWeight: 800, fontSize: '12px', letterSpacing: '0.1em', color: c.textMuted },
  time: { fontSize: '14px', color: c.text, fontVariantNumeric: 'tabular-nums' },
  date: { marginTop: '2px', fontSize: '12px', color: c.textMuted },
};
